import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Card,
  Tabs,
  Table,
  Tag,
  Spin,
  App as AntApp,
  DatePicker,
  Select,
  Input,
  Button,
  Empty,
  Tooltip,
  Alert,
} from 'antd';
import { ExclamationCircleOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import api from '../services/api';
import { formatCurrency } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { ORDER_STATUS_COLORS } from '../utils/orderStatus';
import BatchProfitability from './BatchProfitability';
import './Profitability.css';

const { RangePicker } = DatePicker;

// ─── DTOs ────────────────────────────────────────────────────────────────────

interface OrderRow {
  id: string;
  orderNumber: string;
  manualOrderNumber?: string;
  useManualNumbering?: boolean;
  orderDate?: string;
  customer?: { id?: string; name: string };
  status: string;
  revenue: number;
  totalCost: number;
  margin: number;
  marginPercent: number;
}

type Bucket = 'negative' | 'low' | 'medium' | 'good' | 'excellent' | 'none';

// ─── Constants ───────────────────────────────────────────────────────────────

// Single source of truth for margin-bucket boundaries. Bucket labels in
// reports.json:profitability.buckets reference these values via interpolation.
// Display-only: the alert threshold for low-margin notifications is a separate
// per-tenant setting in backend/src/services/notificationService.js
// (`LOW_MARGIN_THRESHOLD`) — do not couple the two.
const MARGIN_BUCKET_THRESHOLDS = {
  low: 10,
  medium: 25,
  good: 50,
} as const;

// Range picker value tuple (Dayjs|null for each side, or null for the whole tuple).
type RangeValue = [Dayjs | null, Dayjs | null] | null;

// ─── Helpers ─────────────────────────────────────────────────────────────────

function num(value: unknown): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(n) ? n : 0;
}

function marginBucket(totalCost: number, marginPercent: number): Bucket {
  if (totalCost <= 0) return 'none';
  if (marginPercent < 0) return 'negative';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.low) return 'low';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.medium) return 'medium';
  if (marginPercent < MARGIN_BUCKET_THRESHOLDS.good) return 'good';
  return 'excellent';
}

// Locale-aware compact currency (e.g. "1,2 mln zł" / "PLN 1.2M") for KPI cards.
// Detail table still uses full precision via formatCurrency.
function formatCompactPLN(amount: number, lang: string): string {
  const locale = lang.toLowerCase().startsWith('pl') ? 'pl-PL' : 'en-US';
  try {
    return new Intl.NumberFormat(locale, {
      notation: 'compact',
      maximumFractionDigits: 1,
      style: 'currency',
      currency: 'PLN',
      currencyDisplay: locale === 'pl-PL' ? 'symbol' : 'code',
    }).format(amount);
  } catch {
    return formatCurrency(amount, 'PLN');
  }
}

function displayOrderNumber(o: Pick<OrderRow, 'orderNumber' | 'manualOrderNumber' | 'useManualNumbering'>): string {
  return o.useManualNumbering && o.manualOrderNumber ? o.manualOrderNumber : o.orderNumber;
}

// ─── Component ───────────────────────────────────────────────────────────────

const Profitability: React.FC = () => {
  const { t, i18n } = useTranslation(['reports', 'orders', 'common']);
  const { message } = AntApp.useApp();

  const [ordersLoading, setOrdersLoading] = useState(false);
  const [orders, setOrders] = useState<OrderRow[]>([]);

  // ── Filter state (client-side; orders are already fully loaded) ─────────
  const [dateRange, setDateRange] = useState<RangeValue>(null);
  const [statusFilter, setStatusFilter] = useState<string[]>([]);
  const [customerSearch, setCustomerSearch] = useState<string>('');
  const [hideZeroCost, setHideZeroCost] = useState<boolean>(false);
  const [bucketFilter, setBucketFilter] = useState<Bucket[]>([]);

  useEffect(() => {
    loadOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadOrders() {
    setOrdersLoading(true);
    try {
      const res = await api.get('/orders', {
        params: { isQuote: 'false', sortBy: 'orderDate', sortOrder: 'desc' },
      });
      const list = Array.isArray(res.data) ? res.data : [];
      const normalised: OrderRow[] = list.map((o: OrderRow) => ({
        ...o,
        revenue: num(o.revenue),
        totalCost: num(o.totalCost),
        margin: num(o.margin),
        marginPercent: num(o.marginPercent),
      }));
      setOrders(normalised);
    } catch {
      message.error(t('common:messages.error'));
    } finally {
      setOrdersLoading(false);
    }
  }

  const compact = (amount: number) => formatCompactPLN(amount, i18n.language || 'pl');

  // ── Distinct statuses present in current dataset ────────────────────────
  const availableStatuses = useMemo(() => {
    const set = new Set<string>();
    orders.forEach((o) => o.status && set.add(o.status));
    return Array.from(set);
  }, [orders]);

  // ── Filtered orders (client-side) ───────────────────────────────────────
  // Keep the bucket filter separate so its counts remain visible after a
  // bucket is selected. This also lets operators combine it with the other
  // filters without making the remaining bucket chips disappear.
  const filteredWithoutBucket = useMemo(() => {
    return orders.filter((o) => {
      if (hideZeroCost && o.totalCost <= 0) return false;
      if (statusFilter.length > 0 && !statusFilter.includes(o.status)) return false;
      if (customerSearch) {
        const q = customerSearch.trim().toLowerCase();
        if (!o.customer?.name?.toLowerCase().includes(q)) return false;
      }
      if (dateRange && (dateRange[0] || dateRange[1]) && o.orderDate) {
        const d = dayjs(o.orderDate);
        if (dateRange[0] && d.isBefore(dateRange[0].startOf('day'))) return false;
        if (dateRange[1] && d.isAfter(dateRange[1].endOf('day'))) return false;
      }
      return true;
    });
  }, [orders, hideZeroCost, statusFilter, customerSearch, dateRange]);

  const filtered = useMemo(() => {
    if (bucketFilter.length === 0) return filteredWithoutBucket;
    return filteredWithoutBucket.filter((o) => (
      bucketFilter.includes(marginBucket(o.totalCost, o.marginPercent))
    ));
  }, [filteredWithoutBucket, bucketFilter]);

  // ── Aggregated KPI from filtered set ────────────────────────────────────
  const kpi = useMemo(() => {
    const totalRevenue = filtered.reduce((s, o) => s + o.revenue, 0);
    const totalCost = filtered.reduce((s, o) => s + o.totalCost, 0);
    const totalMargin = filtered.reduce((s, o) => s + o.margin, 0);
    const marginPercent = totalRevenue > 0 ? (totalMargin / totalRevenue) * 100 : 0;
    const priced = filtered.filter((o) => o.totalCost > 0);
    const ordersWithMargin = priced.length;
    const avgMarginPercent = priced.length > 0
      ? priced.reduce((s, o) => s + o.marginPercent, 0) / priced.length
      : 0;
    const best = priced.length > 0
      ? priced.reduce((a, b) => (b.margin > a.margin ? b : a))
      : null;
    const worst = priced.length > 0
      ? priced.reduce((a, b) => (b.margin < a.margin ? b : a))
      : null;
    return {
      totalRevenue,
      totalCost,
      totalMargin,
      marginPercent,
      ordersWithMargin,
      totalOrders: filtered.length,
      avgMarginPercent,
      best: best && best.margin > 0 ? best : null,
      worst: worst && worst.margin < 0 ? worst : null,
    };
  }, [filtered]);

  // ── Top customers by margin ─────────────────────────────────────────────
  const topCustomers = useMemo(() => {
    const map = new Map<string, { name: string; margin: number; orderCount: number }>();
    filtered.forEach((o) => {
      if (o.totalCost <= 0) return;
      const key = o.customer?.id || o.customer?.name || '—';
      const name = o.customer?.name || '—';
      const existing = map.get(key) || { name, margin: 0, orderCount: 0 };
      existing.margin += o.margin;
      existing.orderCount += 1;
      map.set(key, existing);
    });
    return Array.from(map.values())
      .filter((c) => c.margin > 0)
      .sort((a, b) => b.margin - a.margin)
      .slice(0, 5);
  }, [filtered]);

  const attentionOrders = useMemo(() => filtered
    .filter((o) => o.totalCost <= 0 || o.margin < 0 || o.marginPercent < MARGIN_BUCKET_THRESHOLDS.low)
    .sort((a, b) => {
      if (a.margin < 0 && b.margin >= 0) return -1;
      if (b.margin < 0 && a.margin >= 0) return 1;
      if (a.totalCost <= 0 && b.totalCost > 0) return -1;
      if (b.totalCost <= 0 && a.totalCost > 0) return 1;
      return a.marginPercent - b.marginPercent;
    })
    .slice(0, 4), [filtered]);

  const dailyTrend = useMemo(() => {
    const days = new Map<string, { revenue: number; margin: number }>();
    filtered.forEach((o) => {
      if (!o.orderDate) return;
      const key = dayjs(o.orderDate).format('YYYY-MM-DD');
      const value = days.get(key) || { revenue: 0, margin: 0 };
      value.revenue += o.revenue;
      value.margin += o.totalCost > 0 ? o.margin : 0;
      days.set(key, value);
    });
    return Array.from(days.entries()).sort(([a], [b]) => a.localeCompare(b)).slice(-12)
      .map(([date, values]) => ({ date, ...values }));
  }, [filtered]);

  // ── Margin by status breakdown ──────────────────────────────────────────
  // Sorted by |margin| so the highest-impact status (whether wins or losses)
  // is shown first — managers care about where money moves most, regardless of sign.
  const byStatus = useMemo(() => {
    const map = new Map<string, { status: string; margin: number; orderCount: number }>();
    filtered.forEach((o) => {
      if (o.totalCost <= 0) return;
      const existing = map.get(o.status) || { status: o.status, margin: 0, orderCount: 0 };
      existing.margin += o.margin;
      existing.orderCount += 1;
      map.set(o.status, existing);
    });
    return Array.from(map.values())
      .sort((a, b) => Math.abs(b.margin) - Math.abs(a.margin));
  }, [filtered]);

  const totalAbsMarginForShare = useMemo(
    () => topCustomers.reduce((s, c) => s + c.margin, 0) || 1,
    [topCustomers],
  );
  const totalAbsStatusMargin = useMemo(
    () => byStatus.reduce((s, c) => s + Math.abs(c.margin), 0) || 1,
    [byStatus],
  );

  // Distribution after the regular filters, before the bucket filter — drives
  // the clickable chips and keeps all available choices visible.
  const bucketCounts = useMemo(() => {
    const counts: Record<Bucket, number> = {
      negative: 0, low: 0, medium: 0, good: 0, excellent: 0, none: 0,
    };
    filteredWithoutBucket.forEach((o) => {
      counts[marginBucket(o.totalCost, o.marginPercent)] += 1;
    });
    return counts;
  }, [filteredWithoutBucket]);

  function toggleBucket(bucket: Bucket) {
    setBucketFilter((current) => (
      current.includes(bucket)
        ? current.filter((selected) => selected !== bucket)
        : [...current, bucket]
    ));
  }

  function resetFilters() {
    setDateRange(null);
    setStatusFilter([]);
    setCustomerSearch('');
    setHideZeroCost(false);
    setBucketFilter([]);
  }

  // ── Orders table columns ────────────────────────────────────────────────
  const orderColumns = useMemo(() => [
    {
      title: t('reports:profitability.colOrder'),
      key: 'orderNumber',
      width: 130,
      sorter: (a: OrderRow, b: OrderRow) =>
        displayOrderNumber(a).localeCompare(displayOrderNumber(b)),
      render: (_: unknown, record: OrderRow) => (
        <Link to={`/app/orders/${record.id}`}>{displayOrderNumber(record)}</Link>
      ),
    },
    {
      title: t('reports:profitability.colCustomer'),
      key: 'customer',
      sorter: (a: OrderRow, b: OrderRow) =>
        (a.customer?.name || '').localeCompare(b.customer?.name || ''),
      render: (_: unknown, record: OrderRow) => record.customer?.name ?? '—',
    },
    {
      title: t('reports:profitability.colOrderDate'),
      dataIndex: 'orderDate',
      key: 'orderDate',
      width: 120,
      defaultSortOrder: 'descend' as const,
      sorter: (a: OrderRow, b: OrderRow) =>
        (a.orderDate ? dayjs(a.orderDate).valueOf() : 0) -
        (b.orderDate ? dayjs(b.orderDate).valueOf() : 0),
      render: (v?: string) => (v ? dayjs(v).format('YYYY-MM-DD') : '—'),
    },
    {
      title: t('reports:profitability.colStatus'),
      dataIndex: 'status',
      key: 'status',
      width: 160,
      sorter: (a: OrderRow, b: OrderRow) => a.status.localeCompare(b.status),
      render: (status: string) => (
        <Tag color={ORDER_STATUS_COLORS[status] ?? 'default'}>
          {t(`orders:statusOptions.${status}`, { defaultValue: status })}
        </Tag>
      ),
    },
    {
      title: t('reports:profitability.colRevenue'),
      dataIndex: 'revenue',
      key: 'revenue',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => a.revenue - b.revenue,
      render: (v: number) => formatCurrency(v, 'PLN'),
    },
    {
      title: t('reports:profitability.colCost'),
      dataIndex: 'totalCost',
      key: 'totalCost',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => a.totalCost - b.totalCost,
      render: (v: number) => (v > 0 ? formatCurrency(v, 'PLN') : <span className="profitability-margin-cell--muted">—</span>),
    },
    {
      title: t('reports:profitability.colMargin'),
      dataIndex: 'margin',
      key: 'margin',
      align: 'right' as const,
      width: 140,
      sorter: (a: OrderRow, b: OrderRow) => a.margin - b.margin,
      render: (v: number, record: OrderRow) => {
        if (record.totalCost <= 0) return <span className="profitability-margin-cell--muted">—</span>;
        const cls = v < 0
          ? 'profitability-margin-cell profitability-margin-cell--negative'
          : 'profitability-margin-cell profitability-margin-cell--positive';
        return <span className={cls}>{formatCurrency(v, 'PLN')}</span>;
      },
    },
    {
      title: t('reports:profitability.colMarginPercent'),
      dataIndex: 'marginPercent',
      key: 'marginPercent',
      align: 'right' as const,
      width: 110,
      sorter: (a: OrderRow, b: OrderRow) => a.marginPercent - b.marginPercent,
      render: (v: number, record: OrderRow) => {
        if (record.totalCost <= 0) return <span className="profitability-margin-cell--muted">—</span>;
        const cls = v < 0
          ? 'profitability-margin-cell profitability-margin-cell--negative'
          : 'profitability-margin-cell profitability-margin-cell--positive';
        return <span className={cls}>{formatLocalizedPercent(v, i18n.language)}</span>;
      },
    },
  ], [i18n.language, t]);

  function rowClassName(record: OrderRow): string {
    const bucket = marginBucket(record.totalCost, record.marginPercent);
    return `profitability-row profitability-row--${bucket}`;
  }

  const trendChart = (() => {
    if (dailyTrend.length < 2) return <Empty description={t('reports:profitability.trend.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />;
    const width = 720;
    const height = 210;
    const pad = 22;
    const max = Math.max(...dailyTrend.flatMap((d) => [d.revenue, Math.max(0, d.margin)]), 1);
    const points = (key: 'revenue' | 'margin') => dailyTrend.map((d, index) => {
      const x = pad + (index / (dailyTrend.length - 1)) * (width - pad * 2);
      const y = height - pad - (Math.max(0, d[key]) / max) * (height - pad * 2);
      return `${x},${y}`;
    }).join(' ');
    return (
      <div className="profitability-trend">
        <div className="profitability-trend__legend">
          <span><i className="is-revenue" />{t('reports:profitability.trend.revenue')}</span>
          <span><i className="is-margin" />{t('reports:profitability.trend.margin')}</span>
        </div>
        <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={t('reports:profitability.trend.title')}>
          {[0, 1, 2, 3].map((line) => <line key={line} x1={pad} x2={width - pad} y1={pad + line * 52} y2={pad + line * 52} className="profitability-trend__grid" />)}
          <polyline points={points('revenue')} className="profitability-trend__line is-revenue" />
          <polyline points={points('margin')} className="profitability-trend__line is-margin" />
        </svg>
        <div className="profitability-trend__dates">
          <span>{dayjs(dailyTrend[0].date).format('D MMM')}</span>
          <span>{dayjs(dailyTrend[dailyTrend.length - 1].date).format('D MMM')}</span>
        </div>
      </div>
    );
  })();

  // ── KPI strip ───────────────────────────────────────────────────────────
  const kpiStrip = (
    <section className="profitability-overview">
      <header className="profitability-overview__header">
        <div>
          <span className="profitability-overview__eyebrow">
            {t('reports:profitability.overviewEyebrow')}
          </span>
          <h2>{t('reports:profitability.overviewTitle')}</h2>
        </div>
        <span className="profitability-overview__scope">
          {t('reports:profitability.filters.showingCount', {
            shown: filtered.length,
            total: orders.length,
          })}
        </span>
      </header>

      <div className="profitability-overview__grid">
        <article className={`profitability-balance ${kpi.totalMargin < 0 ? 'is-negative' : 'is-positive'}`}>
          <span className="profitability-balance__label">
            {t('reports:profitability.kpiMargin')}
          </span>
          <strong className="profitability-balance__value">{compact(kpi.totalMargin)}</strong>
          <span className="profitability-balance__exact">
            {formatCurrency(kpi.totalMargin, 'PLN')}
          </span>
          <div className="profitability-balance__footer">
            <span>
              <small>{t('reports:profitability.kpiMarginPercent')}</small>
              <strong>{formatLocalizedPercent(kpi.marginPercent, i18n.language)}</strong>
            </span>
            <span>
              <small>{t('reports:profitability.kpiAvgMargin')}</small>
              <strong>{formatLocalizedPercent(kpi.avgMarginPercent, i18n.language)}</strong>
            </span>
            <span>
              <small>{t('reports:profitability.kpiOrdersWithMargin')}</small>
              <strong>{kpi.ordersWithMargin}/{kpi.totalOrders}</strong>
            </span>
          </div>
        </article>

        <div className="profitability-overview__components">
          <article className="profitability-component-card is-revenue">
            <span>{t('reports:profitability.kpiRevenue')}</span>
            <strong>{compact(kpi.totalRevenue)}</strong>
            <small>{formatCurrency(kpi.totalRevenue, 'PLN')}</small>
          </article>
          <article className="profitability-component-card is-cost">
            <span>{t('reports:profitability.kpiCost')}</span>
            <strong>{compact(kpi.totalCost)}</strong>
            <small>{formatCurrency(kpi.totalCost, 'PLN')}</small>
          </article>
        </div>

        <div className="profitability-signals">
          <Tooltip title={t('reports:profitability.kpiBestOrderHint')}>
            <article className="profitability-signal is-best">
              <span>{t('reports:profitability.kpiBestOrder')}</span>
              <strong>{kpi.best ? compact(kpi.best.margin) : t('reports:profitability.kpiBestOrderEmpty')}</strong>
              {kpi.best ? (
                <Link to={`/app/orders/${kpi.best.id}`}>
                  {displayOrderNumber(kpi.best)} · {formatLocalizedPercent(kpi.best.marginPercent, i18n.language)}
                </Link>
              ) : <small>—</small>}
            </article>
          </Tooltip>
          <Tooltip title={t('reports:profitability.kpiWorstOrderHint')}>
            <article className="profitability-signal is-worst">
              <span>{t('reports:profitability.kpiWorstOrder')}</span>
              <strong>{kpi.worst ? compact(kpi.worst.margin) : t('reports:profitability.kpiWorstOrderEmpty')}</strong>
              {kpi.worst ? (
                <Link to={`/app/orders/${kpi.worst.id}`}>
                  {displayOrderNumber(kpi.worst)} · {formatLocalizedPercent(kpi.worst.marginPercent, i18n.language)}
                </Link>
              ) : <small>—</small>}
            </article>
          </Tooltip>
        </div>
      </div>
    </section>
  );

  const decisionRow = (
    <div className="profitability-decision-row">
      <Card className="profitability-chart-card" title={t('reports:profitability.trend.title')}>{trendChart}</Card>
      <Card className="profitability-attention-card" title={<span>{t('reports:profitability.attention.title')} <Tag color={attentionOrders.length ? 'error' : 'success'}>{attentionOrders.length}</Tag></span>}>
        {attentionOrders.length === 0 ? <Alert type="success" showIcon message={t('reports:profitability.attention.clear')} /> : attentionOrders.map((o) => {
          const missing = o.totalCost <= 0;
          const loss = !missing && o.margin < 0;
          return (
            <Link className={`profitability-attention-item ${loss ? 'is-loss' : ''}`} to={`/app/orders/${o.id}`} key={o.id}>
              {loss ? <ExclamationCircleOutlined /> : <WarningOutlined />}
              <span>
                <strong>{loss ? t('reports:profitability.attention.loss') : missing ? t('reports:profitability.attention.missingCosts') : t('reports:profitability.attention.lowMargin')}</strong>
                <small>{displayOrderNumber(o)} · {o.customer?.name || '—'}</small>
              </span>
              <b>{missing ? '—' : loss ? formatCurrency(o.margin, 'PLN') : formatLocalizedPercent(o.marginPercent, i18n.language)}</b>
            </Link>
          );
        })}
      </Card>
    </div>
  );

  // ── Widgets row (top customers + status breakdown) ──────────────────────
  const widgetsRow = (
    <div className="profitability-widgets-row">
      <Card
        className="profitability-widget-card"
        size="small"
        title={t('reports:profitability.widgets.topCustomers')}
      >
        {topCustomers.length === 0 ? (
          <Empty
            description={t('reports:profitability.widgets.topCustomersEmpty')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          topCustomers.map((c, index) => {
            const share = (c.margin / totalAbsMarginForShare) * 100;
            return (
              <div className="profitability-widget-row" key={`${c.name}-${index}`}>
                <div className="profitability-widget-row__label">
                  <span className="profitability-widget-row__label-main">{c.name}</span>
                  <span className="profitability-widget-row__label-sub">
                    {t('reports:profitability.widgets.orderCount', { count: c.orderCount })}
                    {' · '}
                    {t('reports:profitability.widgets.shareOfMargin', {
                      percent: formatLocalizedNumber(share, 1, i18n.language),
                    })}
                  </span>
                </div>
                <div className="profitability-widget-row__value">
                  {compact(c.margin)}
                </div>
                <div className="profitability-widget-row__bar">
                  <div
                    className="profitability-widget-row__bar-fill"
                    style={{ width: `${Math.min(100, Math.max(2, share))}%` }}
                  />
                </div>
              </div>
            );
          })
        )}
      </Card>

      <Card
        className="profitability-widget-card"
        size="small"
        title={t('reports:profitability.widgets.byStatus')}
      >
        {byStatus.length === 0 ? (
          <Empty
            description={t('reports:profitability.widgets.byStatusEmpty')}
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
        ) : (
          byStatus.map((s) => {
            const share = (Math.abs(s.margin) / totalAbsStatusMargin) * 100;
            const isNegative = s.margin < 0;
            return (
              <div className="profitability-widget-row" key={s.status}>
                <div className="profitability-widget-row__label">
                  <span className="profitability-widget-row__label-main">
                    <Tag color={ORDER_STATUS_COLORS[s.status] ?? 'default'} style={{ marginRight: 0 }}>
                      {t(`orders:statusOptions.${s.status}`, { defaultValue: s.status })}
                    </Tag>
                  </span>
                  <span className="profitability-widget-row__label-sub">
                    {t('reports:profitability.widgets.orderCount', { count: s.orderCount })}
                  </span>
                </div>
                <div
                  className="profitability-widget-row__value"
                  style={{ color: isNegative ? '#cf1322' : '#389e0d' }}
                >
                  {compact(s.margin)}
                </div>
                <div className="profitability-widget-row__bar">
                  <div
                    className="profitability-widget-row__bar-fill"
                    style={{
                      width: `${Math.min(100, Math.max(2, share))}%`,
                      background: isNegative
                        ? 'linear-gradient(90deg, #ff7875, #cf1322)'
                        : 'linear-gradient(90deg, #95de64, #389e0d)',
                    }}
                  />
                </div>
              </div>
            );
          })
        )}
      </Card>
    </div>
  );

  // ── Bucket legend (uses the same row-tint dots) ─────────────────────────
  const bucketOrder: Bucket[] = ['negative', 'low', 'medium', 'good', 'excellent', 'none'];
  const bucketLegend = (
    <div className="profitability-bucket-legend">
      {bucketOrder
        .filter((b) => bucketCounts[b] > 0)
        .map((b) => (
          <button
            key={b}
            type="button"
            className={`profitability-bucket-chip ${bucketFilter.includes(b) ? 'is-active' : ''}`}
            aria-pressed={bucketFilter.includes(b)}
            onClick={() => toggleBucket(b)}
          >
            <span className={`profitability-bucket-dot profitability-bucket-dot--${b}`} />
            {t(`reports:profitability.buckets.${b}`, {
              low: MARGIN_BUCKET_THRESHOLDS.low,
              medium: MARGIN_BUCKET_THRESHOLDS.medium,
              good: MARGIN_BUCKET_THRESHOLDS.good,
            })}
            <span className="profitability-bucket-chip__count">{bucketCounts[b]}</span>
          </button>
        ))}
    </div>
  );

  // ── Filter bar ──────────────────────────────────────────────────────────
  const filterBar = (
    <Card className="profitability-filters-card" size="small">
      <div className="profitability-filters">
        <div className="profitability-filters__field">
          <span className="profitability-filters__field-label">
            {t('reports:profitability.filters.dateRange')}
          </span>
          <RangePicker
            value={dateRange}
            onChange={(range) => setDateRange(range as RangeValue)}
            allowEmpty={[true, true]}
            style={{ minWidth: 240 }}
          />
        </div>
        <div className="profitability-filters__field">
          <span className="profitability-filters__field-label">
            {t('reports:profitability.filters.status')}
          </span>
          <Select
            mode="multiple"
            maxTagCount="responsive"
            value={statusFilter}
            onChange={setStatusFilter}
            placeholder={t('reports:profitability.filters.statusPlaceholder')}
            style={{ minWidth: 220 }}
            options={availableStatuses.map((s) => ({
              value: s,
              label: t(`orders:statusOptions.${s}`, { defaultValue: s }),
            }))}
            allowClear
          />
        </div>
        <div className="profitability-filters__field">
          <span className="profitability-filters__field-label">
            {t('reports:profitability.filters.customer')}
          </span>
          <Input
            value={customerSearch}
            onChange={(e) => setCustomerSearch(e.target.value)}
            placeholder={t('reports:profitability.filters.customerPlaceholder')}
            allowClear
            style={{ minWidth: 200 }}
          />
        </div>
        <div className="profitability-filters__field">
          <span className="profitability-filters__field-label">
            {t('reports:profitability.filters.costScope')}
          </span>
          <Select
            value={hideZeroCost ? 'withCosts' : 'all'}
            onChange={(value) => setHideZeroCost(value === 'withCosts')}
            style={{ minWidth: 210 }}
            options={[
              {
                value: 'all',
                label: t('reports:profitability.filters.costScopeAll'),
              },
              {
                value: 'withCosts',
                label: t('reports:profitability.filters.costScopeWithCosts'),
              },
            ]}
          />
        </div>
        <Button onClick={resetFilters} size="small">
          {t('reports:profitability.filters.reset')}
        </Button>
        <span className="profitability-filters__counter">
          {t('reports:profitability.filters.showingCount', {
            shown: filtered.length,
            total: orders.length,
          })}
        </span>
      </div>
    </Card>
  );

  // ── Tabs ────────────────────────────────────────────────────────────────
  const ordersTab = (
    <div className="profitability-page">
      {filterBar}
      {kpiStrip}
      {decisionRow}
      {widgetsRow}
      {bucketLegend}
      {ordersLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
        </div>
      ) : (
        <Table<OrderRow>
          dataSource={filtered}
          columns={orderColumns}
          rowKey="id"
          rowClassName={rowClassName}
          size="small"
          scroll={{ x: 1100 }}
          pagination={{
            showSizeChanger: true,
            pageSizeOptions: ['10', '25', '50', '100'],
            defaultPageSize: 25,
          }}
        />
      )}
    </div>
  );

  const tabItems = [
    {
      key: 'orders',
      label: t('reports:profitability.tabOrders'),
      children: ordersTab,
    },
    {
      key: 'batches',
      label: t('reports:profitability.tabBatches'),
      children: <BatchProfitability embedded />,
    },
  ];

  return (
    <div>
      <Tabs items={tabItems} />
    </div>
  );
};

export default Profitability;
